import React, {
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';
import Popover from 'core/components/Popover';
import Tooltip from 'core/components/Tooltip';
import Empty from 'core/components/Empty';
import IconButton from 'core/components/IconButton';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Spinner from 'pages/Dashboard/components/Spinner';
import Button from 'pages/Dashboard/components/Button';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  useGetUnassociatedInsuranceCards,
} from 'views/EMR/components/Insurance/hooks';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import {
  Row,
} from '@tanstack/react-table';

type Props = {
  patientId?: number;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onConfirm: (mediaList: MultiMediaResponse[]) => void | Promise<void>;
  multiple?: boolean;
  disabledMediaIds?: number[];
};

type SelectionCellProps = {
  mediaId?: number;
  isSelected: boolean;
  multiple: boolean;
};

type FileNameCellProps = SelectionCellProps & {
  row: Row<MultiMediaResponse>;
};

type GetColumnsParams = {
  selectedMediaIdsSet: Set<number>;
  multiple: boolean;
  handlePreviewMediaClick: (media: MultiMediaResponse) => void;
};

function SelectionCell({
  mediaId,
  isSelected,
  multiple,
}: SelectionCellProps) {
  return multiple ? (
    <Checkbox
      color="secondary"
      id={`${mediaId ?? 0}`}
      checked={isSelected}
      onChange={noop}
    />
  ) : (
    <div
      className={clsx(
        'h-3 w-3 rounded-full border border-gray-300',
        { 'border-blue-500 bg-blue-500': isSelected },
      )}
    />
  );
}

function FileNameCell({
  row,
  mediaId,
  isSelected,
  multiple,
}: FileNameCellProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-row items-center gap-2 truncate">
      <SelectionCell
        mediaId={mediaId}
        isSelected={isSelected}
        multiple={multiple}
      />
      <div className="min-w-0 flex-1 truncate text-xs font-medium">
        <Tooltip title={row.original.fileName}>
          <span>{row.original.fileName ?? 'Insurance card'}</span>
        </Tooltip>
      </div>
    </div>
  );
}

function getColumns({
  selectedMediaIdsSet,
  multiple,
  handlePreviewMediaClick,
}: GetColumnsParams): ColumnDef<MultiMediaResponse>[] {
  return [
    {
      id: 'fileName',
      accessorKey: 'fileName',
      header: 'Name',
      meta: { autoSized: true },
      cell: ({ row }) => (
        <FileNameCell
          row={row}
          mediaId={row.original.mediaId}
          isSelected={selectedMediaIdsSet.has(row.original.mediaId ?? 0)}
          multiple={multiple}
        />
      ),
    },
    {
      id: 'type',
      accessorKey: 'mimeType',
      header: 'Type',
      size: 120,
      cell: ({ row }) => (
        <div className="truncate text-xs text-gray-500">
          <Tooltip title={row.original.mimeType}>
            <span>{row.original.mimeType ?? '--'}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      id: 'preview',
      header: '',
      size: 44,
      cell: ({ row }) => (
        <div className="ml-auto">
          <IconButton
            tooltip="Preview"
            onClick={(event) => {
              event.stopPropagation();
              handlePreviewMediaClick(row.original);
            }}
          >
            <PreviewOutlined className="!h-4 !w-4 text-gray-400" />
          </IconButton>
        </div>
      ),
    },
  ];
}

export default function UnassociatedInsuranceCardSelect({
  patientId,
  anchorEl,
  onClose,
  onConfirm,
  multiple = true,
  disabledMediaIds = [],
}: Props) {
  const handlePreviewMediaClick = usePreviewMediaClick();
  const [selectedMediaIds, setSelectedMediaIds] = useState<number[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const {
    data: mediaList = [],
    isLoading,
  } = useGetUnassociatedInsuranceCards(patientId ?? 0);

  const selectedMediaIdsSet = useMemo(
    () => new Set(selectedMediaIds),
    [selectedMediaIds],
  );
  const disabledMediaIdsSet = useMemo(
    () => new Set(disabledMediaIds),
    [disabledMediaIds],
  );
  const selectedMediaList = useMemo(
    () => mediaList.filter((media) => selectedMediaIdsSet.has(media.mediaId ?? 0)),
    [mediaList, selectedMediaIdsSet],
  );

  const columns = useMemo(() => getColumns({
    handlePreviewMediaClick,
    multiple,
    selectedMediaIdsSet,
  }), [handlePreviewMediaClick, multiple, selectedMediaIdsSet]);

  const toggleMediaSelection = (mediaId: number) => {
    const isDisabled = disabledMediaIdsSet.has(mediaId);

    if (!isDisabled) {
      if (!multiple) {
        setSelectedMediaIds([mediaId]);
      } else if (selectedMediaIdsSet.has(mediaId)) {
        setSelectedMediaIds((currentIds) => currentIds.filter((id) => id !== mediaId));
      } else {
        setSelectedMediaIds((currentIds) => [...currentIds, mediaId]);
      }
    }
  };

  const handleConfirm = async () => {
    if (selectedMediaList.length > 0) {
      setIsConfirming(true);

      try {
        await onConfirm(selectedMediaList);
        onClose();
      } finally {
        setIsConfirming(false);
      }
    }
  };

  const confirmSingleMedia = async (media: MultiMediaResponse) => {
    setIsConfirming(true);

    try {
      await onConfirm([media]);
      onClose();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={onClose}
      disableScrollLock
      disableAutoFocus
      disableRestoreFocus
      classes={{
        paper: '!min-w-[640px] !max-h-[400px]',
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
    >
      <div className="flex h-full flex-col gap-2.5 p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-800">
            Select Unassociated Cards
          </div>
          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Spinner size={16} />
              Loading cards...
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-hidden">
          <Table<MultiMediaResponse>
            data={mediaList}
            loading={isLoading}
            getRowId={(row) => row.mediaId}
            columns={columns}
            itemSize={44}
            className="!max-h-72 !pb-0"
            classes={{
              row: (row: Row<MultiMediaResponse>) => clsx({
                'bg-selected-entity text-black': selectedMediaIdsSet.has(
                  row.original.mediaId ?? 0,
                ),
                'opacity-50': disabledMediaIdsSet.has(row.original.mediaId ?? 0),
              }),
              scrollContainer: 'justify-center items-center',
              header: '!px-4',
              outerRow: '!w-[calc(100%-16px)] left-4',
              windowsClassName: '!w-full',
            }}
            onRowClick={(table, row) => {
              toggleMediaSelection(row.original.mediaId ?? 0);
            }}
            rowProps={(row) => ({
              tooltip: disabledMediaIdsSet.has(row.original.mediaId ?? 0)
                ? 'Already selected for this insurance'
                : undefined,
              onDoubleClick: () => {
                const mediaId = row.original.mediaId ?? 0;

                if (!multiple && !disabledMediaIdsSet.has(mediaId)) {
                  setSelectedMediaIds([mediaId]);
                  confirmSingleMedia(row.original);
                }
              },
            })}
            loadingSlot="Loading insurance cards..."
            emptySlot={(
              <Empty>
                <div className="text-sm text-gray-500">
                  No unassociated insurance cards available.
                </div>
              </Empty>
            )}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
          >
            Close
          </Button>
          <Tooltip title="Select at least one card" hide={!isNil(selectedMediaList[0])}>
            <span>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color="primary"
                disabled={selectedMediaList.length === 0 || isConfirming}
              >
                {isConfirming ? 'Attaching...' : 'Attach'}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
    </Popover>
  );
}
